import { ProductSelectionGroup } from '@brenger/api-client';
import {
  getIsWeightInRanges,
  getWeightRangeForValue,
  getWeightRangesWithinBounds,
  WEIGHT_RANGES,
  WeightRange,
} from '@brenger/utils';
import React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { WeightRangeSelect } from '../../../../brenger-shared-ui/components/form/WeightRangeSelect';

interface Props {
  value: WeightRange | null;
  onChange: (value: WeightRange) => void;
  productGroups: ProductSelectionGroup[];
  setCheck: (v: boolean) => void;
}

export const ItemWeight: React.FC<Props> = ({ value, onChange, productGroups, setCheck }) => {
  const minArr = productGroups.map(group => group.items?.[0]?.weight_min_kg).filter(Boolean) as number[];
  const maxArr = productGroups.map(group => group.items?.[0]?.weight_max_kg).filter(Boolean) as number[];
  const min = Math.max(...minArr);
  const max = Math.max(...maxArr);
  const weightRanges = !minArr.length && !maxArr.length ? WEIGHT_RANGES : getWeightRangesWithinBounds({ min, max });

  React.useEffect(() => {
    // This effect takes care of selecting the suggested weight as default
    // So if there is a value, we don't need to do anything
    if (value) return;
    const highestSuggestedWeight = Math.max(...productGroups.map(group => group.items?.[0]?.weight_suggested_kg || 0));
    // NOTE: Somehow the form doesn't accept the immediate on mount update.
    setTimeout(() => {
      onChange(getWeightRangeForValue(highestSuggestedWeight));
      setCheck(true);
    }, 250);
  }, [value]);

  React.useEffect(() => {
    // This effect checks if the selected weight is still in range
    // If no value or still in range, we don't need to do anything
    if (!value || getIsWeightInRanges(value.min, weightRanges)) return;
    onChange(weightRanges[0]);
    setCheck(true);
  }, [value, JSON.stringify(weightRanges)]);

  return (
    <div
      className="w-full flex items-center justify-between"
      style={{ borderRadius: '10px', border: '1px solid #aebfd4', padding: '8px 14px' }}
    >
      <div className="w-full">
        <div>
          <Translate id="fields.weight_range.label" />
        </div>
        <div className="text-grey-500 text-sm -mt-1">
          <Translate id="fields.weight_range.instructions" />
        </div>
      </div>
      <WeightRangeSelect
        value={value}
        onChange={v => {
          onChange(v);
          setCheck(false);
        }}
        options={weightRanges}
      />
    </div>
  );
};
